import { useState } from "react";
import { ArrowRight, Sun, X } from "lucide-react";
import type { InSnipCard } from "../types";

/**
 * Catch me up: a finite, calm five-story ritual for readers who avoid the feed because of
 * how news makes them feel. One gentle story per screen, a deliberate full stop at the end,
 * and no infinite scroll anywhere. The anti-doomscroll.
 * House style: no em dashes or en dashes anywhere in copy.
 */
export function CatchMeUp({
  cards,
  onClose,
  onOpenFull,
  onDone,
}: {
  cards: InSnipCard[];
  onClose: () => void;
  /** Open a story in the full reader (closes this overlay). */
  onOpenFull?: (id: string) => void;
  /** Fired once when the reader reaches the full stop. */
  onDone?: () => void;
}) {
  const [i, setI] = useState(0);
  const [doneFired, setDoneFired] = useState(false);
  const finished = i >= cards.length;
  const card = cards[i];

  if (finished && !doneFired && cards.length) {
    setDoneFired(true);
    onDone?.();
  }

  return (
    <div className="drawer-scrim center" role="dialog" aria-modal="true" aria-label="Catch me up" onClick={onClose}>
      <section className={`v3-panel calm-panel${finished ? " done" : ""}`} onClick={(e) => e.stopPropagation()}>
        <header className="v3-panel-head">
          <span className="v3-eyebrow"><Sun size={14} aria-hidden="true" /> Catch me up</span>
          <button type="button" className="icon-button ghost" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {!cards.length ? (
          <div className="calm-done-hero">
            <div className="calm-sun" aria-hidden="true" />
            <h3>You are already caught up</h3>
            <p>Nothing gentle is waiting right now. Enjoy the quiet.</p>
            <button type="button" className="ob3-cta" onClick={onClose}>Back to your day</button>
          </div>
        ) : !finished && card ? (
          <div className="calm-story" key={card.id}>
            <div className="calm-progress" aria-hidden="true">
              {cards.map((c, k) => (
                <span key={c.id} className={k < i ? "calm-pip done" : k === i ? "calm-pip active" : "calm-pip"} />
              ))}
            </div>
            <span className="calm-meta">
              {String(card.category ?? "Today")} · {i + 1} of {cards.length}
              {card.goodNews ? <em className="calm-good">Good news</em> : null}
            </span>
            <h3 className="calm-headline">{card.headline}</h3>
            <p className="calm-essence">{card.essence}</p>
            {card.whyItMatters ? (
              <p className="calm-why">
                <span>Why it matters</span>
                {card.whyItMatters}
              </p>
            ) : null}
            <div className="calm-actions">
              <button type="button" className="ob3-cta" onClick={() => setI(i + 1)}>
                {i + 1 < cards.length ? "Next story" : "Finish"} <ArrowRight size={15} aria-hidden="true" />
              </button>
              {onOpenFull ? (
                <button type="button" className="ob3-ghost" onClick={() => onOpenFull(card.id)}>
                  Read in full
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="calm-done-hero">
            <div className="calm-sun" aria-hidden="true" />
            <h3>You are all caught up</h3>
            <p>
              {cards.length} stories, a few minutes, zero doom. That is the whole point.
              Go live your day.
            </p>
            <button type="button" className="ob3-cta" onClick={onClose}>Back to your day</button>
          </div>
        )}
      </section>
    </div>
  );
}
